// Data glue for the engineer's in-progress job sheet (Session APF): ties the IndexedDB adapter
// (db.ts) to the one screen that uses it. The React layer calls these; it never touches IndexedDB
// directly. Mirrors photosStore.ts / messagesStore.ts.
//
// THE PROBLEM THIS EXISTS FOR (docs/reports/PWA-AUDIT-2026-09-21.md, findings 3 and fix list item
// 4). The job sheet kept everything the engineer entered in React component state and nowhere
// else. Leaving the sheet lost all of it: a tap on the bottom navigation, the Fault Assistant link,
// the camera pushing the tab out of memory, or the phone being locked for an hour mid-visit and the
// PWA being evicted. It also blocked the next piece of work outright: an ARRIVAL tap of the lift's
// POP tag at the start of a visit has to still be on the sheet when it is submitted an hour later.
//
// EVERY FUNCTION THAT TOUCHES THE STORE IS BEST-EFFORT, AND THAT IS THE CONTRACT, NOT A SHORTCUT.
// Each one catches every error and resolves. A draft is a convenience. The sheet itself must work
// exactly as it did before there was one, so a failure to read or write a draft (private browsing,
// a full disk, a database another tab is part way through upgrading, a runtime with no IndexedDB at
// all) must never break the sheet, block it, or surface as an unhandled rejection. The four older
// JobDetail test files are the standing proof: jsdom has no indexedDB, none of them mocks this
// module, and they pass unchanged because these calls degrade to "there is no draft".
//
// NOT CLEARED ON SIGN-OUT, DELIBERATELY. A draft is the engineer's OWN unfinished work, exactly
// like the three outboxes, and src/lib/offlineCache.ts explains why those are never cleared on an
// auth change. The case is stronger here: the session cookie is a fixed seven days and can run out
// mid-visit, so clearing on sign-out would destroy the sheet at exactly the moment the engineer is
// sent to sign in again. That is also why db.ts gives this store no clear() for anyone to call.
import { sheetDraftStore } from "./db";
import type {
  ChecklistStatus,
  CompleteJobSheetDraftFields,
  CoveredItemStatus,
  InsuranceAnswerDraft,
  JobSheetDraft,
  JobSheetDraftFields,
  LolerItemOutcome,
  PartUsedEntry,
  SignatureSkipReason,
  TapStamp,
} from "./types";

// A stale draft is worse than none. A half-filled sheet from last week, put back on the screen as
// if it belonged to today's visit, invites the engineer to submit somebody else's Tuesday. Seven
// days matches the longest a job realistically sits part-done (a visit abandoned on a Friday and
// finished the following week) without keeping a customer's signature on a phone indefinitely.
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function isStale(draft: JobSheetDraft, now: number): boolean {
  const savedAt = typeof draft.savedAt === "string" ? Date.parse(draft.savedAt) : Number.NaN;
  // An unreadable stamp is treated as stale rather than as fresh: a row whose age cannot be told
  // cannot be shown to be recent, and the safe direction is to drop it.
  if (!Number.isFinite(savedAt)) return true;
  return now - savedAt > DRAFT_MAX_AGE_MS;
}

// The stored draft for a job, or undefined when there is none worth restoring. A draft older than
// seven days, or one with no readable fields, is DELETED as well as refused, so it cannot be offered
// again on the next open.
export async function loadDraft(jobId: string): Promise<JobSheetDraft | undefined> {
  try {
    const draft = await sheetDraftStore.getDraft(jobId);
    if (!draft) return undefined;
    const unusable = typeof draft.fields !== "object" || draft.fields === null;
    if (unusable || isStale(draft, Date.now())) {
      await sheetDraftStore.deleteDraft(jobId);
      return undefined;
    }
    return draft;
  } catch {
    return undefined;
  } finally {
    // Opening any sheet is also the moment the other rows get looked at. Never awaited: the sheet
    // being opened should not wait on housekeeping for jobs it has nothing to do with.
    void sweepStaleDrafts();
  }
}

// Write (or replace) a job's draft. `savedAt` is whatever the caller stamped.
export async function saveDraft(draft: JobSheetDraft): Promise<void> {
  try {
    await sheetDraftStore.putDraft(draft);
  } catch {
    // Best-effort. The sheet on the screen is untouched, and the next change tries again.
  }
}

// Drop a job's draft: its sheet was submitted, its job was declined, or the job turned out to be
// finished already. Deleting a draft that is not there is not an error.
export async function clearDraft(jobId: string): Promise<void> {
  try {
    await sheetDraftStore.deleteDraft(jobId);
  } catch {
    // Best-effort. A draft left behind is refused by loadDraft once it is a week old, or ignored and
    // cleared on the next open if the job is finished.
  }
}

// Remove every draft that has sat untouched for more than seven days, whichever job it belongs to.
//
// WHY loadDraft's OWN CHECK IS NOT ENOUGH. That check only ever sees the row for the job being
// opened. A job the office reassigned or completed mid-visit leaves this engineer's list and is
// never opened again, so without a sweep its draft, which may hold a customer's signature, would
// sit on the phone for good and the seven-day rule would be true only of rows somebody revisits.
export async function sweepStaleDrafts(): Promise<void> {
  try {
    await sheetDraftStore.deleteDraftsSavedBefore(new Date(Date.now() - DRAFT_MAX_AGE_MS).toISOString());
  } catch {
    // Best-effort, like everything else here.
  }
}

// ---- Reading a draft back: the pure half ---------------------------------------------------------
//
// A later build will add fields to the sheet, and an engineer can be half way through a visit when
// the app updates under them. So nothing below trusts the shape of what it is handed. A MISSING key
// means that field's normal starting value, and so does a key holding the wrong kind of thing: one
// field that cannot be read costs that field, never the rest of the sheet.

const STATUSES: readonly ChecklistStatus[] = ["pass", "fail", "advisory", "na"];
const SKIP_REASONS: readonly SignatureSkipReason[] = ["customer_unavailable", "remote_site", "emergency"];

const isString = (v: unknown): v is string => typeof v === "string";
const isBoolean = (v: unknown): v is boolean => typeof v === "boolean";
const isStatus = (v: unknown): v is ChecklistStatus => isString(v) && (STATUSES as readonly string[]).includes(v);

function isPart(v: unknown): v is PartUsedEntry {
  if (typeof v !== "object" || v === null) return false;
  const p = v as Record<string, unknown>;
  return (
    isString(p.description) &&
    typeof p.quantity === "number" &&
    Number.isFinite(p.quantity) &&
    (p.unitCost === undefined || (typeof p.unitCost === "number" && Number.isFinite(p.unitCost))) &&
    (p.partCode === undefined || isString(p.partCode))
  );
}

// A keyed record with every unreadable entry dropped. A record that is already clean is handed back
// AS IS rather than copied, so a draft read straight back compares equal to what was stored and the
// sheet does not rewrite a draft nobody has changed.
function cleanRecord<T>(value: unknown, ok: (v: unknown) => v is T): Record<string, T> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.every(([, v]) => ok(v))) return value as Record<string, T>;
  return Object.fromEntries(entries.filter(([, v]) => ok(v))) as Record<string, T>;
}

function cleanParts(value: unknown): PartUsedEntry[] {
  if (!Array.isArray(value)) return [];
  return value.every(isPart) ? (value as PartUsedEntry[]) : value.filter(isPart);
}

const text = (v: unknown): string => (isString(v) ? v : "");

// A POP tag tap stamp (Session APF), or null when the slot holds anything else. The time has to
// parse: it pre-fills a time field and can become the recorded arrival or departure of the visit, so
// a stamp that cannot be read as an instant is worth less than no stamp at all. A readable stamp is
// handed back AS IS, for the same reason cleanRecord hands a clean record back.
function cleanStamp(value: unknown): TapStamp | null {
  if (typeof value !== "object" || value === null) return null;
  const s = value as Record<string, unknown>;
  return isString(s.at) && Number.isFinite(Date.parse(s.at)) && isBoolean(s.live) ? (value as TapStamp) : null;
}

// Every field of a stored draft, with anything missing or unreadable replaced by the value the
// sheet starts from. `oohDefault` is the one starting value that is not a constant: the out-of-hours
// toggle starts on the basis the call-out was logged on (JobView.calloutOutOfHours).
const isCoveredStatus = (v: unknown): v is CoveredItemStatus => v === "pass" || v === "fail" || v === "na";

const isLolerOutcome = (v: unknown): v is LolerItemOutcome => v === "checked" || v === "needs_work" || v === "no_access";
const OUTCOMES = ["verified_present", "not_present", "needs_parts", "needs_return_visit", "unable_to_check"];
// One answer a draft holds, or not one at all: an unreadable outcome, a note that is not text or photo indexes that are not whole
// numbers are dropped rather than guessed at, as every other record here is.
const isInsuranceAnswer = (v: unknown): v is InsuranceAnswerDraft => {
  if (typeof v !== "object" || v === null) return false;
  const a = v as Record<string, unknown>;
  return (
    (a.outcome === undefined || (isString(a.outcome) && OUTCOMES.includes(a.outcome))) &&
    isString(a.note) &&
    (a.severity === undefined || a.severity === "advisory" || a.severity === "immediate") &&
    Array.isArray(a.photoIndexes) &&
    a.photoIndexes.every((n) => Number.isInteger(n) && (n as number) >= 0)
  );
};

export function draftFieldsWithDefaults(
  raw: JobSheetDraftFields | null | undefined,
  oohDefault: boolean,
): CompleteJobSheetDraftFields {
  const f: Record<string, unknown> = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  return {
    statuses: cleanRecord(f.statuses, isStatus),
    notes: cleanRecord(f.notes, isString),
    // Session AQB (D78): the covered-item answers. An unrecognised status is dropped rather than
    // guessed at, as every other record here is.
    coveredStatuses: cleanRecord(f.coveredStatuses, isCoveredStatus),
    coveredNotes: cleanRecord(f.coveredNotes, isString),
    // Session BAQ: LOLER item answers. Unanswered items are simply absent.
    lolerAnswers: cleanRecord(f.lolerAnswers, isLolerOutcome),
    lolerNotes: cleanRecord(f.lolerNotes, isString),
    // Session BBF: the insurance check's answers.
    insuranceAnswers: cleanRecord(f.insuranceAnswers, isInsuranceAnswer),
    areaConfirmed: cleanRecord(f.areaConfirmed, isBoolean),
    areaNotes: cleanRecord(f.areaNotes, isString),
    defectsText: text(f.defectsText),
    advisory: text(f.advisory),
    notesText: text(f.notesText),
    svcArrival: text(f.svcArrival),
    svcDeparture: text(f.svcDeparture),
    visitDateYmd: /^\d{4}-\d{2}-\d{2}$/.test(text(f.visitDateYmd)) ? text(f.visitDateYmd) : "",
    lateReason: text(f.lateReason),
    lateReasonText: text(f.lateReasonText),
    tapArrival: cleanStamp(f.tapArrival),
    tapDeparture: cleanStamp(f.tapDeparture),
    travelMinsText: text(f.travelMinsText),
    returnTravelMinsText: text(f.returnTravelMinsText),
    ooh: isBoolean(f.ooh) ? f.ooh : oohDefault,
    faultFound: text(f.faultFound),
    workCarriedOut: text(f.workCarriedOut),
    liftStatus: text(f.liftStatus),
    parts: cleanParts(f.parts),
    // Only an image data URL is put back. It is shown in an <img> and then submitted as the
    // customer's signature, and anything else in that slot would do neither job.
    signature: isString(f.signature) && f.signature.startsWith("data:image/") ? f.signature : null,
    skipReason: isString(f.skipReason) && (SKIP_REASONS as readonly string[]).includes(f.skipReason) ? (f.skipReason as SignatureSkipReason) : "",
    signerName: text(f.signerName),
    skipping: f.skipping === true,
    raHazards: cleanRecord(f.raHazards, isBoolean),
    raOther: text(f.raOther),
    raControl: text(f.raControl),
    raSignedAt: isString(f.raSignedAt) && f.raSignedAt ? f.raSignedAt : null,
    acceptedLocal: f.acceptedLocal === true,
    ramsAckLocal: f.ramsAckLocal === true,
  };
}

const anyTrue = (r: Record<string, boolean>) => Object.values(r).some(Boolean);
const anyText = (r: Record<string, string>) => Object.values(r).some((v) => v.trim() !== "");

// Has the engineer actually DONE anything on the sheet itself? The acceptance flag is left out on
// purpose: see draftHasContent below, which adds it back.
//
// SO IS THE RAMS ACKNOWLEDGEMENT, since Session APF folded it into the Accept tap. It used to be
// counted here, which was right while the only way to set it was a tick on the open sheet. Once one
// tap on the accept screen sets both flags, a job accepted the evening before would otherwise read
// as a visit that had begun the evening before: the restore pins the ORIGINAL opening time as the
// service visit's arrival whenever there is work on the sheet, and shows "We kept what you had
// already entered" over a sheet nobody had entered anything on. An acknowledgement is no more an
// arrival than an acceptance is, however it was given, so it sits with the acceptance below.
//
// `skipping` is left out too. It records that the "Customer cannot sign?" panel is open, which is
// where the sheet is showing something rather than something the engineer entered; the reason they
// pick there IS counted. It is still saved and restored alongside real content, because a restored
// reason has to come back with its panel open to be seen.
export function draftHasWork(fields: JobSheetDraftFields, oohDefault: boolean): boolean {
  const f = draftFieldsWithDefaults(fields, oohDefault);
  return (
    Object.keys(f.statuses).length > 0 ||
    anyText(f.notes) ||
    Object.keys(f.lolerAnswers).length > 0 ||
    Object.values(f.insuranceAnswers).some((a) => a.outcome !== undefined || a.note.trim() !== "" || a.photoIndexes.length > 0) ||
    anyTrue(f.areaConfirmed) ||
    anyText(f.areaNotes) ||
    [
      f.defectsText,
      f.advisory,
      f.notesText,
      f.svcArrival,
      f.svcDeparture,
      f.visitDateYmd,
      f.lateReason,
      f.lateReasonText,
      f.travelMinsText,
      f.returnTravelMinsText,
      f.faultFound,
      f.workCarriedOut,
      f.liftStatus,
      f.signerName,
      f.raOther,
      f.raControl,
    ].some((v) => v.trim() !== "") ||
    // A POP tag tap (Session APF) is work in its own right, even if the engineer has since cleared
    // the time it pre-filled: the tap happened, and a sheet reopened without it would go back to
    // asking for a tap that has already been made.
    f.tapArrival !== null ||
    f.tapDeparture !== null ||
    f.ooh !== oohDefault ||
    f.parts.length > 0 ||
    f.signature !== null ||
    f.skipReason !== "" ||
    anyTrue(f.raHazards) ||
    f.raSignedAt !== null
  );
}

// Is there anything here worth a row in the store? A sheet that has merely been LOOKED at writes no
// draft, so opening ten jobs to read their addresses does not leave ten rows behind. An acceptance
// on its own does count, and so does a RAMS acknowledgement on its own (the fallback tick on a job
// accepted before Session APF): made with no signal, either exists nowhere else yet.
export function draftHasContent(fields: JobSheetDraftFields, oohDefault: boolean): boolean {
  return fields.acceptedLocal === true || fields.ramsAckLocal === true || draftHasWork(fields, oohDefault);
}
